import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  TextField,
  Paper,
  Grid,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  Divider,
  Chip,
  Stack,
  Avatar,
  Tooltip,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import WorkIcon from '@mui/icons-material/Work';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Team and role data structure
const TEAM_ROLES = {
  'Testing Team': [
    'QA Manager',
    'Test Lead',
    'Manual Test Engineer',
    'Automation Test Engineer',
    'Performance Tester',
    'Security Tester',
    'Test Analyst'
  ],
  'Development Team': [
    'Software Architect',
    'Frontend Developer',
    'Backend Developer',
    'Full-Stack Developer',
    'Mobile App Developer',
    'API Developer',
    'Database Developer'
  ],
  'DevOps Team': [
    'DevOps Engineer',
    'Build & Release Engineer',
    'Site Reliability Engineer (SRE)',
    'CI/CD Pipeline Specialist',
    'Cloud Engineer',
    'Infrastructure as Code Engineer',
    'Monitoring & Logging Specialist'
  ],
  'IT Support Team': [
    'IT Support Technician',
    'System Administrator',
    'Network Administrator',
    'Help Desk Analyst',
    'Hardware Support Engineer',
    'IT Security Support',
    'Software Installation Specialist'
  ],
  'Security Team': [
    'Security Analyst',
    'Penetration Tester',
    'Security Engineer',
    'Security Architect',
    'SOC Analyst',
    'IAM Specialist',
    'Compliance Officer'
  ],
  'UI/UX Team': [
    'UI Designer',
    'UX Designer',
    'UX Researcher',
    'Interaction Designer',
    'Visual Designer',
    'Information Architect',
    'Accessibility Specialist'
  ],
  'Product Design Team':[
    'Product Designer',
    'Design Systems Manager',
    'Motion Designer',
    'Design Researcher',
  ],
  'Graphic Design Team':[
    'Graphic Designer',
    'Visual Designer',
    'Brand Designer',
    'Illustrator',
    'Animation Designer'
  ],
  'Product Management Team':[
    'Product Manager',
    'Product Owner',
    'Product Analyst',
    'Associate Product Manager',
    'Growth Product Manager',
  ],
  'Project Management Team':[
    'Project Manager',
    'Project Coordinator',
    'Project Lead',
    'Risk Manager'
  ],
  'Business Analysis Team':[
    'Business Analyst',
    'System Analyst',
    'Data Analyst',
    'Process Analyst'
  ],
  'Marketing Team':[
    'Marketing Manager',
    'Digital Marketing Specialist',
  ],
  'Sales Team':[
    'Sales Executive',
    'Sales Manager',
    'Business Development Executive',
    'Inside Sales Representative',
    
  ],
  'Social Media Team':[
    'Social Media Manager',
    'Content Creator',
    'Social Media Analyst',
    'Video Editor'
  ],
  'Custom Team': [] 
};

const TeamService = {

  createTeam: async (teamData) => {
    const token = localStorage.getItem("token");

    return await axios.post(
      `${API_URL}/api/createteam`,
      teamData,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
  },

  updateTeamMember: async (teamMemberId, updateData) => {
    const token = localStorage.getItem("token");

    return await axios.put(
      `${API_URL}/api/createteam/team-members/${teamMemberId}`,
      updateData,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
  },

  getEmployees: async (employeeId) => {
    const token = localStorage.getItem("token");
    const query = employeeId ? `?employee_id=${employeeId}` : '';

    return await axios.get(
      `${API_URL}/api/addteam/employees${query}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
  },

  getTeamMember: async (teamId, employeeId) => {
    const token = localStorage.getItem("token");

    return await axios.get(
      `${API_URL}/api/createteam/${teamId}/employee/${employeeId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
  },

  getTeam: async (teamId) => {
    const token = localStorage.getItem("token");

    return await axios.get(
      `${API_URL}/api/createteam/teams/${teamId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
  }
};

const CreateTeamForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if we are in edit mode
  const isEditing = location.state?.isEditing || false;
  const editingTeamId = location.state?.teamId || '';
  const editingTeamName = location.state?.teamName || '';
  const editingEmployeeId = location.state?.memberId || '';
  const editingRole = location.state?.memberRole || '';
  const projectId = location.state?.projectId || '';
  
  const [selectedTeams, setSelectedTeams] = useState(isEditing ? [editingTeamName] : []);
  const [customTeamName, setCustomTeamName] = useState('');
  const [teamData, setTeamData] = useState({});
  const [employees, setEmployees] = useState([]);
  const [teamMemberId, setTeamMemberId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditing);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [teamName, setTeamName] = useState('');

  const fetchManagerId = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;
      const payload = JSON.parse(atob(token.split(".")[1]));
      const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/user/current?company_id=${company_id}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      if (response.data && response.data.employee_id) {
        return response.data.employee_id;
      }
      return null;
    } catch (err) {
      //console.error("Error fetching current user:", err);
      return null;
    }
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const token = localStorage.getItem("token"); // get JWT token
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }

        const managerId = await fetchManagerId();
        const response = await TeamService.getEmployees(managerId);
        setEmployees(response.data);
      } catch (error) {
        //console.error('Error fetching employees:', error);
        setAlert({
          open: true,
          message: 'Failed to load employees',
          severity: 'error'
        });
      }
    };

    fetchEmployees();
  }, []);

  useEffect(() => {
    const loadExistingData = async () => {
      if (isEditing && editingTeamId && editingEmployeeId) {
        try {
          const token = localStorage.getItem("token"); // get JWT token
          if (!token) {
            alert("No authentication token found. Please log in.");
            return;
          }
        
          const payload = JSON.parse(atob(token.split('.')[1]));
          const company_id = payload.company_id;
          setInitialLoading(true);
          
          const teamResponse = await TeamService.getTeam(editingTeamId, company_id);
          const currentTeamData = teamResponse.data;
          
          const memberResponse = await TeamService.getTeamMember(editingTeamId, editingEmployeeId, company_id);
          const memberData = memberResponse.data;
          
          if (memberData) {
            setTeamMemberId(memberData.employee_id || memberData.memberInfo?.employee_id || editingEmployeeId);
            setTeamName(currentTeamData.team_name || editingTeamName);
            const currentTeamType = currentTeamData.team_type || editingTeamName;
            setSelectedTeams([currentTeamType]);
            
            const memberRole = memberData.role || memberData.memberInfo?.role || editingRole;
            const isStandardRole = TEAM_ROLES[currentTeamType]?.includes(memberRole);
            
            setTeamData({
              [currentTeamType]: [{
                employee_id: editingEmployeeId,
                role: memberRole,
                roleOption: isStandardRole ? memberRole : '__CUSTOM_ROLE__',
                customRole: isStandardRole ? '' : memberRole
              }]
            });
          }
        } catch (error) {
          //console.error('Error loading team member data:', error);
          setAlert({
            open: true,
            message: 'Failed to load team member data: ' + (error.response?.data?.message || error.message),
            severity: 'error'
          });
        } finally {
          setInitialLoading(false);
        }
      }
    };
    
    loadExistingData();
  }, [isEditing, editingTeamId, editingEmployeeId, editingTeamName, editingRole]);

  const handleTeamSelectionChange = (event) => {
    if (isEditing) return;
    
    const value = event.target.value;
    const newSelectedTeams = typeof value === 'string' ? value.split(',') : value;
    
    setSelectedTeams(newSelectedTeams);
    
    // Initialize team data for newly selected teams
    const newTeamData = { ...teamData };
    newSelectedTeams.forEach(team => {
      if (!newTeamData[team]) {
        newTeamData[team] = [{ 
          employee_id: '', 
          role: '', 
          roleOption: team === 'Custom Team' ? '__CUSTOM_ROLE__' : '', 
          customRole: '' 
        }];
      }
    });
    
    // Remove data for unselected teams
    Object.keys(newTeamData).forEach(team => {
      if (!newSelectedTeams.includes(team)) {
        delete newTeamData[team];
      }
    });
    
    setTeamData(newTeamData);
    
    // Clear custom team name if not selected
    if (!newSelectedTeams.includes('Custom Team')) {
      setCustomTeamName('');
    }
  };

  const handleAddMember = (teamType) => {
    if (isEditing) return;
    
    setTeamData(prev => ({
      ...prev,
      [teamType]: [
        ...(prev[teamType] || []), 
        { 
          employee_id: '', 
          role: '', 
          roleOption: teamType === 'Custom Team' ? '__CUSTOM_ROLE__' : '', 
          customRole: '' 
        }
      ]
    }));
  };

  const handleRemoveMember = (teamType, memberIndex) => {
    if (isEditing) return;
    
    setTeamData(prev => ({
      ...prev,
      [teamType]: prev[teamType].filter((_, index) => index !== memberIndex)
    }));
  };

  const handleMemberChange = (teamType, memberIndex, field, value) => {
    setTeamData(prev => ({
      ...prev,
      [teamType]: (prev[teamType] || []).map((member, index) => 
        index === memberIndex ? { ...member, [field]: value } : member
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    // --- Extract company_id from token ---
    const token = localStorage.getItem("token");
    if (!token) {
      setAlert({
        open: true,
        message: "No authentication token found. Please log in.",
        severity: "error"
      });
      return;
    }
    const payload = JSON.parse(atob(token.split(".")[1]));
    const company_id = payload.company_id;
  
    if (!company_id) {
      setAlert({
        open: true,
        message: "Company ID not found in token.",
        severity: "error"
      });
      return;
    }
  
    // --- Validation ---
  
    if (!teamName.trim()) {
      setAlert({
        open: true,
        message: "Team name is required",
        severity: "error"
      });
      return;
    }
  
    if (selectedTeams.length === 0) {
      setAlert({
        open: true,
        message: "Please select at least one team type",
        severity: "error"
      });
      return;
    }
  
    if (selectedTeams.includes("Custom Team") && !customTeamName.trim()) {
      setAlert({
        open: true,
        message: "Custom team type name is required when Custom Team is selected",
        severity: "error"
      });
      return;
    }
  
    for (const teamType of selectedTeams) {
      const displayName = teamType === 'Custom Team' ? (customTeamName.trim() || 'Custom Team') : teamType;
      const members = teamData[teamType] || [];
      if (members.length === 0) {
        setAlert({
          open: true,
          message: `Please add at least one member to ${displayName}`,
          severity: "error"
        });
        return;
      }
  
      const hasEmptyMembers = members.some((member) => {
        const effectiveRole = member.roleOption === '__CUSTOM_ROLE__' 
          ? (member.customRole || '') 
          : (member.role || '');
        return !member.employee_id || !effectiveRole.trim();
      });

      if (hasEmptyMembers) {
        setAlert({
          open: true,
          message: `All members in ${displayName} must have an employee and valid role assigned`,
          severity: "error"
        });
        return;
      }
    }
  
    setLoading(true);
  
    try {
      if (isEditing) {
        // --- Update single member ---
        const employeeIdToUpdate =
          teamMemberId ||
          teamData[selectedTeams[0]]?.[0]?.employee_id ||
          editingEmployeeId;
  
        const currentMember = teamData[selectedTeams[0]]?.[0];
        const finalRole = (currentMember?.roleOption === '__CUSTOM_ROLE__' 
          ? (currentMember?.customRole || '') 
          : (currentMember?.role || '')).trim();

        await TeamService.updateTeamMember(employeeIdToUpdate, {
          role: finalRole,
          team_id: editingTeamId
        });
  
        setAlert({
          open: true,
          message: "Team member role updated successfully!",
          severity: "success"
        });
  
        setTimeout(() => {
          navigate("/manageteam", {
            state: {
              message: "Team member role updated successfully!",
              severity: "success",
              projectId: projectId
            }
          });
        }, 1500);
      } else {
        const managerId = await fetchManagerId();
        // --- Create teams for each selected type ---
        const promises = selectedTeams.map(async (teamType) => {
          const actualTeamType = teamType === "Custom Team" ? customTeamName.trim() : teamType;
          const rawMembers = teamData[teamType] || [];
          const members = rawMembers.map(member => ({
            employee_id: member.employee_id,
            role: (member.roleOption === '__CUSTOM_ROLE__' 
              ? (member.customRole || '') 
              : (member.role || '')).trim()
          }));
  
          const uniqueTeamName =
            selectedTeams.length === 1
              ? teamName.trim()
              : `${teamName.trim()} - ${actualTeamType}`;
  
          // --- Send POST request with company_id and manager_id ---
          return axios.post(
            `${API_URL}/api/createteam`,
            {
              team_name: uniqueTeamName,
              team_type: actualTeamType,
              members,
              manager_id: managerId
            },{
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
              }
            }
          );
        });
  
        await Promise.all(promises);
  
        setAlert({
          open: true,
          message: `Successfully created ${selectedTeams.length} team(s)!`,
          severity: "success"
        });
  
        // --- Reset form ---
        setSelectedTeams([]);
        setCustomTeamName("");
        setTeamName("");
        setTeamData({});
      }
    } catch (error) {
      //console.error("Error saving team data:", error);
      setAlert({
        open: true,
        message: error.response?.data?.message || "Failed to save team data",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };
  
  

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  const handleCancel = () => {
    if (isEditing) {
      navigate('/manageteam', { state: { projectId } });
    } else {
      setSelectedTeams([]);
      setCustomTeamName('');
      setTeamName('');
      setTeamData({});
    }
  };

  if (initialLoading) {
    return (
      <Container maxWidth="1500px">
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '60vh',
          borderRadius: 4,
          color: 'black'
        }}>
          <CircularProgress size={60} sx={{ color: 'primary.main', mb: 3 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Loading team member data...
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.8, mt: 1 }}>
            Please wait while we fetch the information
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="1500px" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ 
        borderRadius: 4,
        p: 4,
        mb: 4,
        color: 'black',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        height: '190px',
      }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
         
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: "bold", mb: 1 }}>
              {isEditing ? 'Edit Team Member' : 'Create Teams'}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              {isEditing 
                ? 'Update role and responsibilities for team member' 
                : 'Build multiple teams at once with the right people and roles'
              }
            </Typography>
          </Box>
        </Stack>
        
       
      </Box>

      {/* Main Form Card */}
      <Card sx={{ 
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)', 
        borderRadius: 4,
        overflow: 'visible'
      }}>
        <CardContent sx={{ p: 6 }}>
          <form onSubmit={handleSubmit}>
            {/* Team Configuration Section */}
            <Box sx={{ mb: 6 }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                  <WorkIcon />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: "bold", color: '#2d3748' }}>
                  Team Configuration
                </Typography>
              </Stack>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={selectedTeams.includes('Custom Team') && !isEditing ? 4 : 6}>
                  <TextField
                    fullWidth
                    label="Team Name"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    required
                    disabled={isEditing}
                    placeholder="Enter your team name"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={selectedTeams.includes('Custom Team') && !isEditing ? 4 : 6}>
                  <FormControl 
                    fullWidth 
                    disabled={isEditing}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  >
                    <InputLabel>Select Team Types</InputLabel>
                    <Select
                      multiple
                      value={selectedTeams}
                      onChange={handleTeamSelectionChange}
                      input={<OutlinedInput label="Select Team Types" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip 
                              key={value} 
                              label={value === 'Custom Team' && customTeamName.trim() ? `Custom: ${customTeamName.trim()}` : value} 
                              size="small" 
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {Object.keys(TEAM_ROLES).map((team) => (
                        <MenuItem key={team} value={team}>
                          <Checkbox checked={selectedTeams.indexOf(team) > -1} />
                          <ListItemText primary={team} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {selectedTeams.includes('Custom Team') && !isEditing && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Custom Team Type Name"
                      value={customTeamName}
                      onChange={(e) => setCustomTeamName(e.target.value)}
                      required
                      placeholder="e.g., Data Science Team, AI Research"
                      helperText="Specify the custom team type"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Team Members Section */}
            {selectedTeams.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                  <Avatar sx={{ bgcolor: '#48bb78', width: 40, height: 40 }}>
                    <PersonAddIcon />
                  </Avatar>
                  <Typography variant="h5" sx={{ fontWeight: "bold", color: '#2d3748' }}>
                    Team Members Configuration
                  </Typography>
                </Stack>

                {selectedTeams.map((teamType) => {
                  const displayTeamName = teamType === 'Custom Team' 
                    ? (customTeamName.trim() || 'Custom Team') 
                    : teamType;
                  const currentRoles = TEAM_ROLES[teamType] || [];

                  return (
                    <Accordion key={teamType} defaultExpanded sx={{ mb: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Chip 
                            label={displayTeamName} 
                            color="primary" 
                            variant="outlined"
                            sx={{ borderRadius: 2 }}
                          />
                          <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            {teamData[teamType]?.length || 0} member(s)
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={3}>
                          {!isEditing && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Button 
                                variant="outlined" 
                                startIcon={<AddCircleOutlineIcon />} 
                                onClick={() => handleAddMember(teamType)}
                                sx={{
                                  borderRadius: 2,
                                  borderColor: 'primary.main',
                                  color: 'primary.main',
                                }}
                              >
                                Add Member to {displayTeamName}
                              </Button>
                            </Box>
                          )}

                          {(teamData[teamType] || []).map((member, memberIndex) => {
                            const isCustomSelected = 
                              member.roleOption === '__CUSTOM_ROLE__' ||
                              (teamType === 'Custom Team' && !currentRoles.includes(member.role)) ||
                              (!currentRoles.includes(member.role) && Boolean(member.role));

                            const selectValue = member.roleOption 
                              ? member.roleOption 
                              : (currentRoles.includes(member.role) 
                                ? member.role 
                                : (member.role || teamType === 'Custom Team' ? '__CUSTOM_ROLE__' : ''));

                            return (
                              <Card key={memberIndex} sx={{ 
                                border: '2px solid #e2e8f0',
                                borderRadius: 3,
                                '&:hover': {
                                  borderColor: '#cbd5e0',
                                  boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                                }
                              }}>
                                <CardContent sx={{ p: 4 }}>
                                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                                    <Chip 
                                      label={`Member ${memberIndex + 1}`} 
                                      color="primary" 
                                      variant="outlined"
                                      sx={{ borderRadius: 2 }}
                                    />
                                    {!isEditing && (teamData[teamType]?.length || 0) > 1 && (
                                      <IconButton 
                                        color="error" 
                                        onClick={() => handleRemoveMember(teamType, memberIndex)}
                                        sx={{ ml: 'auto' }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    )}
                                  </Stack>
                                  
                                  <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                      <FormControl fullWidth disabled={isEditing}>
                                        <InputLabel>Select Employee</InputLabel>
                                        <Select
                                          value={member.employee_id}
                                          onChange={(e) => handleMemberChange(teamType, memberIndex, 'employee_id', e.target.value)}
                                          label="Select Employee"
                                          required
                                        >
                                          {employees.map((employee) => (
                                            <MenuItem key={employee.employee_id || employee.id} value={employee.employee_id || employee.id}>
                                              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                <Avatar sx={{ width: 32, height: 32, mr: 2, bgcolor: 'primary.main' }}>
                                                  {employee.name ? employee.name.charAt(0).toUpperCase() : 'N'}
                                                </Avatar>
                                                <Box>
                                                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                    {employee.name || 'Unknown Employee'}
                                                  </Typography>
                                                  <Typography variant="caption" color="text.secondary">
                                                    ID: {employee.employee_id || employee.id}
                                                  </Typography>
                                                </Box>
                                              </Box>
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                    
                                    <Grid item xs={12} md={6}>
                                      <FormControl fullWidth>
                                        <InputLabel>Select Role</InputLabel>
                                        <Select
                                          value={selectValue}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === '__CUSTOM_ROLE__') {
                                              handleMemberChange(teamType, memberIndex, 'roleOption', '__CUSTOM_ROLE__');
                                              handleMemberChange(teamType, memberIndex, 'role', member.customRole || '');
                                            } else {
                                              handleMemberChange(teamType, memberIndex, 'roleOption', val);
                                              handleMemberChange(teamType, memberIndex, 'role', val);
                                              handleMemberChange(teamType, memberIndex, 'customRole', '');
                                            }
                                          }}
                                          label="Select Role"
                                          required
                                        >
                                          {currentRoles.map((role) => (
                                            <MenuItem key={role} value={role}>
                                              <Typography variant="body1">{role}</Typography>
                                            </MenuItem>
                                          ))}
                                          <MenuItem value="__CUSTOM_ROLE__">
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                              <AddCircleOutlineIcon sx={{ mr: 1, fontSize: 20, color: '#48bb78' }} />
                                              <Typography variant="body1" sx={{ color: '#48bb78', fontWeight: 600 }}>
                                                + Custom Role
                                              </Typography>
                                            </Box>
                                          </MenuItem>
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                    
                                    {isCustomSelected && (
                                      <Grid item xs={12}>
                                        <TextField
                                          fullWidth
                                          label="Enter Custom Role"
                                          placeholder="e.g., Senior Software Engineer, Lead Designer, AI Researcher"
                                          value={member.customRole !== undefined ? member.customRole : (member.roleOption === '__CUSTOM_ROLE__' ? '' : member.role || '')}
                                          onChange={(e) => {
                                            const customVal = e.target.value;
                                            handleMemberChange(teamType, memberIndex, 'customRole', customVal);
                                            handleMemberChange(teamType, memberIndex, 'role', customVal);
                                            handleMemberChange(teamType, memberIndex, 'roleOption', '__CUSTOM_ROLE__');
                                          }}
                                          required
                                          helperText="Enter the custom role title for this team member"
                                          sx={{
                                            '& .MuiOutlinedInput-root': {
                                              borderRadius: 2,
                                            },
                                          }}
                                        />
                                      </Grid>
                                    )}
                                  </Grid>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            )}

            <Divider sx={{ my: 4 }} />

            {/* Action Buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              justifyContent: 'flex-end',
              flexWrap: 'wrap'
            }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleCancel}
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                }}
              >
                {isEditing ? 'Cancel' : 'Reset Form'}
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                  },
                }}
              >
                {loading 
                  ? (isEditing ? 'Updating...' : 'Creating...') 
                  : (isEditing ? 'Update Member Role' : `Create ${selectedTeams.length} Team(s)`)
                }
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>

      {/* Success/Error Alert */}
      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseAlert} 
          severity={alert.severity} 
          sx={{ 
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }}
          variant="outlined"
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CreateTeamForm;